import React, { useMemo } from 'react'
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Input } from 'reactstrap'
import { addGSTNumberToWallet, validateGSTNumber } from 'services/api/wallet';
import { RootState } from 'store';
import { GSTValidationResponse } from 'types/wallet';

const GSTInput = () => {

    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);

    const [gstNumber, setGstNumber] = React.useState<string>("");
    const [gstError, setGstError] = React.useState<string | null>(null);

    const [validationData, setValidationData] = React.useState<GSTValidationResponse | null>(null);
    const [validating, setValidating] = React.useState<boolean>(false);
    const [adding, setAdding] = React.useState<boolean>(false);

    const isValidGSTNumber = useMemo(() => {
        // Basic validation for GST number format
        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        return gstRegex.test(gstNumber);
    }, [gstNumber]);

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase().trim();
        if (value === "") {
            setGstError("GST Number cannot be empty.");
        } else if (!isValidGSTNumber) {
            setGstError("Invalid GST Number format.");
        } else {
            setGstError(null);
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setGstNumber(e.target.value.toUpperCase().trim());
    }

    const validateGST = async () => {
        if (!isValidGSTNumber || !selectedAgencyId) return;

        setGstError(null);
        setValidationData(null);
        setValidating(true);
        try {
            const response = await validateGSTNumber(selectedAgencyId, gstNumber);
            setValidationData(response);
        } catch (error: any) {
            const response = error.response?.data as GSTValidationResponse;
            setGstError(response?.message || "Failed to validate GST Number.");
        } finally {
            setValidating(false);
        }
    }

    const addGSTNumber = async () => {
        if (!validationData?.status || !selectedAgencyId || !validationData?.gstin?.id) return;

        setAdding(true);
        try {
            const added = await addGSTNumberToWallet(selectedAgencyId, validationData.gstin.id);
            if (added) {
                toast.success("GST Number added to wallet successfully.");
                // Reset state
                setGstNumber("");
                setValidationData(null);
            } else {
                toast.error("Failed to add GST Number to wallet.");
            }
        } finally {
            setAdding(false);
        }
    }

    return (
        <>
            <div>
                <h6 className='wallet-stat-item-heading'>Add GST</h6>
                <span className="text-muted">
                    We use GSTIN to verify your business and complete payments compliance.
                </span>
                <div className='position-relative'>
                    <Input disabled={!!(validating || validationData)} type="text" placeholder="Enter GST Number" className="mt-3" value={gstNumber} onChange={handleChange} onBlur={handleBlur} />
                    <div className="status-icon">
                        {
                            validating ? (
                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            ) : (
                                validationData ? (
                                    validationData.status ? (
                                        <div className='d-flex gap-2'>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12ZM16.0303 8.96967C16.3232 9.26256 16.3232 9.73744 16.0303 10.0303L11.0303 15.0303C10.7374 15.3232 10.2626 15.3232 9.96967 15.0303L7.96967 13.0303C7.67678 12.7374 7.67678 12.2626 7.96967 11.9697C8.26256 11.6768 8.73744 11.6768 9.03033 11.9697L10.5 13.4393L12.7348 11.2045L14.9697 8.96967C15.2626 8.67678 15.7374 8.67678 16.0303 8.96967Z" fill="#0BAC68" />
                                            </svg>
                                        </div>

                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM13.4142 12L16.7071 8.70711C17.0976 8.31658 17.0976 7.68342 16.7071 7.29289C16.3166 6.90237 15.6834 6.90237 15.2929 7.29289L12 10.5858L8.70711 7.29289C8.31658 6.90237 7.68342 6.90237 7.29289 7.29289C6.90237 7.68342 6.90237 8.31658 7.29289 8.70711L10.5858 12L7.29289 15.2929C6.90237 15.6834 6.90237 16.3166 7.29289 16.7071C7.68342 17.0976 8.31658 17.0976 8.70711 16.7071L12 13.4142L15.2929 16.7071C15.6834 17.0976 16.3166 17.0976 16.7071 16.7071C17.0976 16.3166 17.0976 15.6834 16.7071 15.2929L13.4142 12Z" fill="#E03E2F" />
                                        </svg>
                                    )
                                ) : (
                                    gstNumber && gstError &&
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM13.4142 12L16.7071 8.70711C17.0976 8.31658 17.0976 7.68342 16.7071 7.29289C16.3166 6.90237 15.6834 6.90237 15.2929 7.29289L12 10.5858L8.70711 7.29289C8.31658 6.90237 7.68342 6.90237 7.29289 7.29289C6.90237 7.68342 6.90237 8.31658 7.29289 8.70711L10.5858 12L7.29289 15.2929C6.90237 15.6834 6.90237 16.3166 7.29289 16.7071C7.68342 17.0976 8.31658 17.0976 8.70711 16.7071L12 13.4142L15.2929 16.7071C15.6834 17.0976 16.3166 17.0976 16.7071 16.7071C17.0976 16.3166 17.0976 15.6834 16.7071 15.2929L13.4142 12Z" fill="#E03E2F" />
                                    </svg>
                                )
                            )
                        }
                    </div>

                </div>
                {
                    gstError && <div className="text-danger my-2">{gstError}</div>
                }
                {
                    validationData &&
                    <div className='mt-3 d-flex flex-column gap-2'>
                        <div className="d-flex gap-2">
                            <span className="business-details-label">Business Name:</span>
                            <span className="business-details-value text-success">{validationData.gstin.business_name && validationData.gstin.business_name !== 'NA' ? validationData.gstin.business_name : validationData.gstin.legal_name}</span>
                        </div>
                        <div className="d-flex gap-2">
                            <span className="business-details-label">Business Address:</span>
                            <span className="business-details-value text-success">{validationData.gstin.address}</span>
                        </div>
                    </div>
                }
            </div>
            {
                validationData?.status ? (
                    <button onClick={addGSTNumber} disabled={!isValidGSTNumber} className='btn btn-gradient w-100'>
                        {
                            adding ? 'Confirming...' : 'Confirm'
                        }
                    </button>
                ) : (
                    <button onClick={validateGST} disabled={!isValidGSTNumber} className='btn btn-gradient w-100'>
                        {
                            validating ? 'Validating...' : 'Validate'
                        }
                    </button>
                )

            }
        </>
    )
}

export default GSTInput
